import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SpriteFourStep, SpriteFrame } from './Experimental';
import redSprite from '../images/bulba/characters/Red.png';

const COLS = 12;
const ROWS = 8;
const SPRITE_MULT = 1;

const DIR_TO_SPRITE = {
  ArrowDown:  0,
  ArrowLeft:  1,
  ArrowRight: 2,
  ArrowUp:    3,
};

// Dynamically load all sprites from the bulba folder (mirrors PokeRender pattern)
const spriteCtx = require.context('../images/bulba', false);
const ALL_SPRITES = spriteCtx.keys()
  .filter(k => /\/\d{3}\.png$/.test(k))   // only 3-digit base forms, no variants
  .sort()
  .map(key => {
    const mod = spriteCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('.png', ''), img };
  });

const shinyCtx = require.context('../images/bulba/Shiny', false);
const ALL_SHINY_SPRITES = shinyCtx.keys()
  .filter(k => /\/\d{3}s\.png$/.test(k))  // only 3-digit base shiny forms
  .sort()
  .map(key => {
    const mod = shinyCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('s.png', ''), img };
  });

const DEFAULT_IMG = redSprite;

// Per-direction 4-frame walk cycle for Red. Coordinates are [col, row] matching
// the user-provided layout. The inbetween frame is repeated at positions 1 and 3.
// dirNum order matches DIR_TO_SPRITE: 0=Down, 1=Left, 2=Right, 3=Up
const RED_DIR_FRAMES = [
  [[2,2],[2,1],[2,3],[2,1]], // Down:  step1 → inbetween → step2 → inbetween
  [[0,1],[0,2],[0,3],[0,2]], // Left:  step1 → inbetween → step2 → inbetween
  [[1,1],[1,0],[1,2],[1,0]], // Right: step1 → inbetween → step2 → inbetween
  [[2,0],[0,0],[1,3],[0,0]], // Up:    step1 → inbetween → step2 → inbetween
];

function useAnimFrame() {
  const [count, setCount] = useState(0);
  const requestRef = useRef();
  const prevTimeRef = useRef();

  useEffect(() => {
    const animate = time => {
      if (prevTimeRef.current !== undefined) {
        const delta = time - prevTimeRef.current;
        setCount(prev => (prev + delta * 0.0035) % 4);
      }
      prevTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  return Math.floor(count);
}

export default function Game({ dark }) {
  const [body, setBody] = useState([
    { row: 3, col: 5, direction: 'ArrowRight', sprite: DEFAULT_IMG },
  ]);
  const [showPicker, setShowPicker] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [filter, setFilter] = useState('');
  const [shiny, setShiny] = useState(false);

  const frame = useAnimFrame();

  const MOVES = {
    ArrowUp:    { row: -1, col:  0 },
    ArrowDown:  { row:  1, col:  0 },
    ArrowLeft:  { row:  0, col: -1 },
    ArrowRight: { row:  0, col:  1 },
  };

  const move = useCallback((key) => {
    const delta = MOVES[key];
    if (!delta) return;
    setBody(prev => {
      const newHead = {
        ...prev[0],
        row: Math.max(0, Math.min(ROWS - 1, prev[0].row + delta.row)),
        col: Math.max(0, Math.min(COLS - 1, prev[0].col + delta.col)),
        direction: key,
      };
      const newBody = [newHead];
      for (let i = 1; i < prev.length; i++) {
        newBody.push({ ...prev[i - 1], sprite: prev[i].sprite });
      }
      return newBody;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKey = useCallback((e) => {
    if (!MOVES[e.key]) return;
    e.preventDefault();
    move(e.key);
  }, [move]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const addSegment = (sprite) =>
    setBody(prev => [...prev, { ...prev[prev.length - 1], sprite }]);

  const removeSegment = () =>
    setBody(prev => prev.length > 1 ? prev.slice(0, -1) : prev);

  const cellMap = new Map();
  body.forEach((seg, i) => {
    const key = `${seg.row},${seg.col}`;
    if (!cellMap.has(key)) cellMap.set(key, i);
  });

  const borderColor = dark ? '#555' : '#aaa';
  const bgColor     = dark ? '#1e1e1e' : '#f5f5f5';
  const cellBg      = dark ? '#2a2a2a' : '#fff';
  const textColor   = dark ? '#ccc' : '#444';

  const renderSegment = (f, seg, dirNum) => {
    const rf = 3 - f;
    if (seg.sprite === redSprite) {
      const [col, row] = RED_DIR_FRAMES[dirNum][rf];
      return SpriteFrame(redSprite, row, col, SPRITE_MULT, 3, 4);
    }
    return SpriteFourStep(rf, seg.sprite, 0, dirNum, SPRITE_MULT);
  };

  const trimmed = filter.trim();
  const spritePool = shiny ? ALL_SHINY_SPRITES : ALL_SPRITES;
  const filtered = trimmed
    ? spritePool.filter(s => s.id.includes(trimmed))
    : spritePool;

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Game</h2>
      <p style={{ color: dark ? '#aaa' : '#555', marginBottom: '1rem' }}>
        Use arrow keys or the D-pad to move. Add or remove segments below.
      </p>

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center' }}>
        <button
          onClick={removeSegment}
          disabled={body.length <= 1}
          style={{
            width: 40, height: 40, fontSize: '1.4rem', fontWeight: 'bold',
            borderRadius: '50%', border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: body.length <= 1 ? 'not-allowed' : 'pointer',
            opacity: body.length <= 1 ? 0.4 : 1,
          }}
        >
          −
        </button>

        <span style={{ color: textColor, minWidth: 80 }}>
          {body.length} segment{body.length !== 1 ? 's' : ''}
        </span>

        <button
          onClick={() => { setShowPicker(true); setFilter(''); setShiny(false); }}
          style={{
            width: 40, height: 40, fontSize: '1.4rem', fontWeight: 'bold',
            borderRadius: '50%', border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: 'pointer',
          }}
        >
          +
        </button>
      </div>

      {/* Sprite picker modal */}
      {showPicker && (
        <div
          onClick={() => setShowPicker(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: cellBg,
              border: `2px solid ${borderColor}`,
              borderRadius: 10,
              padding: '1.25rem',
              width: 420,
              maxWidth: '92vw',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '80vh',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexShrink: 0 }}>
              <h3 style={{ color: textColor, margin: 0 }}>
                Choose a Pokémon
              </h3>
              <button
                onClick={() => setShiny(s => !s)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: `2px solid ${shiny ? '#f5c518' : borderColor}`,
                  background: shiny ? (dark ? '#3a3000' : '#fffbe6') : 'transparent',
                  color: shiny ? '#f5c518' : textColor,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  transition: 'all 0.15s',
                }}
              >
                ★ Shiny
              </button>
            </div>

            <input
              autoFocus
              placeholder="Filter by dex # (e.g. 025)"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{
                marginBottom: '0.75rem',
                padding: '6px 10px',
                borderRadius: 6,
                border: `1px solid ${borderColor}`,
                background: dark ? '#1a1a1a' : '#fff',
                color: textColor,
                fontSize: '1rem',
                flexShrink: 0,
                outline: 'none',
              }}
            />

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
              gap: 4,
              overflowY: 'auto',
              paddingRight: 4,
            }}>
              {filtered.map(s => (
                <div
                  key={s.id}
                  title={`#${s.id}`}
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => { addSegment(s.img); setShowPicker(false); setHoveredId(null); }}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderRadius: 6,
                    border: `2px solid ${hoveredId === s.id ? (dark ? '#88aaff' : '#4466cc') : 'transparent'}`,
                    background: hoveredId === s.id ? (dark ? '#333' : '#eef') : 'transparent',
                    transition: 'border-color 0.1s, background 0.1s',
                  }}
                >
                  <div style={{ transform: 'translateY(-6px)', pointerEvents: 'none' }}>
                    {SpriteFourStep(3 - frame, s.img, 0, 0, SPRITE_MULT)}
                  </div>
                  <span style={{
                    fontSize: '0.55rem',
                    color: textColor,
                    textAlign: 'center',
                    marginTop: -4,
                    lineHeight: 1.2,
                  }}>
                    #{s.id}
                  </span>
                </div>
              ))}
              {filtered.length === 0 && (
                <span style={{ color: textColor, gridColumn: '1/-1', padding: '1rem', fontSize: '0.85rem' }}>
                  No sprites match "{filter}"
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'inline-block', border: `2px solid ${borderColor}`, background: bgColor, borderRadius: '6px', padding: '4px' }}>
        {Array.from({ length: ROWS }, (_, r) => (
          <div key={r} style={{ display: 'flex' }}>
            {Array.from({ length: COLS }, (_, c) => {
              const segIndex = cellMap.get(`${r},${c}`);
              const hasSeg = segIndex !== undefined;
              const seg = hasSeg ? body[segIndex] : null;
              const dirNum = hasSeg ? (DIR_TO_SPRITE[seg.direction] ?? 2) : 2;
              const isHead = segIndex === 0;
              return (
                <div
                  key={c}
                  style={{
                    width: 52,
                    height: 52,
                    border: `1px solid ${borderColor}`,
                    background: cellBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: hasSeg && !isHead ? 1 : 1,
                  }}
                >
                  <div style={{ transform: 'translateY(-6px)' }}>
                    {hasSeg && renderSegment(frame, seg, dirNum)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* D-pad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 52px)',
        gridTemplateRows: 'repeat(3, 52px)',
        gap: 4,
        marginTop: '1.5rem',
        justifyContent: 'center',
      }}>
        {[
          { key: null },
          { key: 'ArrowUp',    label: '▲' },
          { key: null },
          { key: 'ArrowLeft',  label: '◀' },
          { key: null },
          { key: 'ArrowRight', label: '▶' },
          { key: null },
          { key: 'ArrowDown',  label: '▼' },
          { key: null },
        ].map((btn, i) => (
          btn.key ? (
            <button
              key={i}
              onPointerDown={e => { e.preventDefault(); move(btn.key); }}
              style={{
                width: 52, height: 52,
                fontSize: '1.2rem',
                border: `2px solid ${borderColor}`,
                borderRadius: 8,
                background: cellBg,
                color: dark ? '#fff' : '#333',
                cursor: 'pointer',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >
              {btn.label}
            </button>
          ) : (
            <div key={i} />
          )
        ))}
      </div>

    </div>
  );
}
